import { Injectable } from '@angular/core';
import { delay, Observable, of, throwError } from 'rxjs';
import { HttpErrorResponse, HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import {
  ApproveTrialDto,
  ApproveTrialResult,
  RejectTrialDto,
  TrialRequest,
  TrialRequestsPage,
  TrialRequestStatus,
} from '@app/core/models/trial-request.interface';

// Mock-mode handler for /admin/signups. Decisions last until the page is reloaded. To see an error in
// the review dialog, approve "Name Taken Motors": it always answers 409 TENANT_NAME_TAKEN, like the API
// does when a workshop with that name already exists.
const DAY_MS = 24 * 60 * 60 * 1000;
const TRIAL_DAYS = 7;

@Injectable({ providedIn: 'root' })
export class TrialRequestMocksService {
  private requests: TrialRequest[] = this.seed();

  handleMockRequest(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    const afterSignups = req.url
      .split('/admin/signups')[1]
      .split('?')[0]
      .split('/')
      .filter(Boolean);
    const id = afterSignups[0] ? parseInt(afterSignups[0], 10) : undefined;
    const action = afterSignups[1];

    if (req.method === 'GET' && id === undefined) return this.list(req);
    if (req.method === 'POST' && id !== undefined && action === 'approve') {
      return this.approve(id, req.body as ApproveTrialDto);
    }
    if (req.method === 'POST' && id !== undefined && action === 'reject') {
      return this.reject(id, req.body as RejectTrialDto);
    }

    throw new Error(`Unsupported mock trial-request request: ${req.method} ${req.url}`);
  }

  private list(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    // The adaptor sends page, limit and status as request params, not in the URL text.
    const page = parseInt(req.params.get('page') || '1', 10);
    const limit = parseInt(req.params.get('limit') || '10', 10);
    const status = req.params.get('status') as TrialRequestStatus | null;

    const matching = this.requests.filter((r) => !status || r.status === status);
    const body: TrialRequestsPage = {
      data: matching.slice((page - 1) * limit, page * limit),
      total: matching.length,
      page,
      limit,
      totalPages: Math.ceil(matching.length / limit),
    };
    return this.respond(body);
  }

  private approve(id: number, dto: ApproveTrialDto): Observable<HttpEvent<any>> {
    const request = this.requests.find((r) => r.id === id);
    if (!request) return this.fail(404, `Trial request ${id} not found`);
    if (request.status !== 'AWAITING_APPROVAL') {
      return this.fail(409, `This request is ${request.status}, so it cannot be approved.`);
    }
    if (request.workshopName === 'Name Taken Motors') {
      return this.fail(409, 'A workshop with this name already exists.');
    }

    const tenantId = 100 + id;
    Object.assign(request, {
      status: 'APPROVED',
      decidedAt: new Date().toISOString(),
      decidedBy: 'mock-staff',
      tenantId,
      workshopName: dto.workshopName ?? request.workshopName,
    });
    const result: ApproveTrialResult = {
      signupId: id,
      tenantId,
      stripeCustomerId: `cus_mock${id}`,
      stripeSubscriptionId: `sub_mock${id}`,
      trialEndsAt: new Date(Date.now() + TRIAL_DAYS * DAY_MS).toISOString(),
    };
    return this.respond(result);
  }

  private reject(id: number, dto: RejectTrialDto): Observable<HttpEvent<any>> {
    const request = this.requests.find((r) => r.id === id);
    if (!request) return this.fail(404, `Trial request ${id} not found`);
    if (request.status !== 'AWAITING_APPROVAL') {
      return this.fail(409, `This request is ${request.status}, so it cannot be rejected.`);
    }
    Object.assign(request, {
      status: 'REJECTED',
      decidedAt: new Date().toISOString(),
      decidedBy: 'mock-staff',
      rejectionReason: dto.reason,
    });
    return this.respond(request);
  }

  private respond<T>(body: T): Observable<HttpEvent<T>> {
    return of(new HttpResponse({ status: 200, body })).pipe(delay(400));
  }

  // Shaped like the API's error body, so the screens read it the way they read a real one.
  private fail(status: number, message: string): Observable<never> {
    const error = new HttpErrorResponse({
      status,
      error: { statusCode: status, details: { message } },
    });
    return throwError(() => error).pipe(delay(400));
  }

  private seed(): TrialRequest[] {
    const ago = (days: number) => new Date(Date.now() - days * DAY_MS).toISOString();
    const base = {
      address: null,
      promoCode: null,
      verifiedAt: null,
      decidedAt: null,
      decidedBy: null,
      rejectionReason: null,
      tenantId: null,
    };
    return [
      {
        ...base,
        id: 1,
        email: 'sam@acmemotors.co.nz',
        workshopName: 'Acme Motors',
        ownerName: 'Sam Owner',
        phone: '021 555 0100',
        address: '12 Main St, Henderson, Auckland',
        planKey: 'WORKSHOP',
        billingInterval: 'MONTH',
        status: 'AWAITING_APPROVAL',
        createdAt: ago(1),
        expiresAt: ago(0),
        verifiedAt: ago(1),
      },
      {
        ...base,
        id: 2,
        email: 'pat@northshore.nz',
        workshopName: 'North Shore Auto',
        ownerName: 'Pat Lee',
        phone: '09 555 0101',
        planKey: 'WORKSHOP_PLUS',
        billingInterval: 'YEAR',
        promoCode: 'WELCOME',
        status: 'AWAITING_APPROVAL',
        createdAt: ago(2),
        expiresAt: ago(1),
        verifiedAt: ago(2),
      },
      {
        ...base,
        id: 3,
        email: 'kim@solo.nz',
        workshopName: 'Kim Mobile Mechanic',
        ownerName: 'Kim Ray',
        phone: '027 555 0102',
        planKey: 'SOLO',
        billingInterval: 'MONTH',
        status: 'AWAITING_APPROVAL',
        createdAt: ago(3),
        expiresAt: ago(2),
        verifiedAt: ago(3),
      },
      {
        ...base,
        id: 4,
        email: 'lee@nametaken.nz',
        workshopName: 'Name Taken Motors',
        ownerName: 'Lee Moss',
        phone: '021 555 0103',
        planKey: 'SOLO',
        billingInterval: 'MONTH',
        status: 'AWAITING_APPROVAL',
        createdAt: ago(1),
        expiresAt: ago(0),
        verifiedAt: ago(1),
      },
      {
        ...base,
        id: 5,
        email: 'jo@fastlane.nz',
        workshopName: 'Fast Lane Garage',
        ownerName: 'Jo Fast',
        phone: '021 555 0104',
        planKey: 'WORKSHOP',
        billingInterval: 'MONTH',
        status: 'APPROVED',
        createdAt: ago(9),
        expiresAt: ago(8),
        verifiedAt: ago(9),
        decidedAt: ago(8),
        decidedBy: 'mock-staff',
        tenantId: 42,
      },
      {
        ...base,
        id: 6,
        email: 'x@mailinator.com',
        workshopName: 'Totally Real Workshop',
        ownerName: 'X',
        phone: '021 555 0105',
        planKey: 'SOLO',
        billingInterval: 'MONTH',
        status: 'REJECTED',
        createdAt: ago(6),
        expiresAt: ago(5),
        verifiedAt: ago(6),
        decidedAt: ago(5),
        decidedBy: 'mock-staff',
        rejectionReason: 'Not a real workshop',
      },
      {
        ...base,
        id: 7,
        email: 'never@clicked.nz',
        workshopName: 'Never Clicked Ltd',
        ownerName: 'Nev Click',
        phone: '021 555 0106',
        planKey: 'SOLO',
        billingInterval: 'MONTH',
        status: 'PENDING_VERIFICATION',
        createdAt: ago(0),
        expiresAt: new Date(Date.now() + DAY_MS).toISOString(),
      },
    ];
  }
}
